import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, ReverLoading } from '@itsrever/design-system'
import { PageHeader, SaveBar } from '@/ui/shared'
import { useEcommerceId } from '@/hooks'
import {
  useGenerateWebhookSecret,
  useRevealWebhookSecret,
  useRevokeWebhookSecret,
  useWebhookSecretHistory
} from '@/application/manage-webhook-secret'
import {
  useSaveWebhookSubscriptions,
  useSendTestWebhooks,
  useWebhookSubscriptions,
  type TestableWebhook,
  type WebhookSubscriptions,
  type WebhookTestResult
} from '@/application/manage-webhook-subscriptions'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import NotFound from '@/ui/pages/404.page'
import {
  isDashboardBlocked,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { BlockedDashboardOverlay } from '@/ui/components/overlays/blocked-dashboard-overlay'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { PbSendTestWebhookResponseFailureEnum } from '@itsrever/rever-api'

// A locale-formatted date is text, not markup: without this i18next turns "31/8/2026"
// into "31&#x2F;8&#x2F;2026", and React renders the entities verbatim.
const RAW_TEXT = { interpolation: { escapeValue: false } }

export default function WebhooksSettings() {
  const ecommerceId = useEcommerceId() ?? ''
  // Remount per store so no revealed secret, draft or test result carries over.
  return <StoreWebhooksSettings key={ecommerceId} ecommerceId={ecommerceId} />
}

function StoreWebhooksSettings({ ecommerceId }: { ecommerceId: string }) {
  const { t } = useTranslation()
  const { isFlagEnabled } = useFeatureFlag(isDashboardBlocked, {
    pageName: 'configuration'
  })
  const profile = useAppSelector((s) => s.userApi.getMe)
  // getMe is dispatched from an effect, so on a direct load the role is not known yet.
  // Deciding before it lands would flash the 404 page at an admin refreshing this URL.
  const loadingProfile =
    profile.loading === 'idle' || profile.loading === 'pending'
  const isAdmin = profile.response.user?.role === Roles.ReadWrite

  const {
    activeSecret,
    group,
    groupEcommerceIds,
    loadingSecrets,
    secretsError,
    reloadSecrets
  } = useWebhookSecretHistory(ecommerceId, isAdmin)
  const { generateSecret, generatedSecret, generating } =
    useGenerateWebhookSecret(ecommerceId)
  const { revealSecret, revealedSecret, revealing, clearRevealed } =
    useRevealWebhookSecret(ecommerceId)
  const { revokeSecret, revoking } = useRevokeWebhookSecret(ecommerceId)
  const showDialog = useDialog()
  const {
    subscriptions,
    loadingSubscriptions,
    subscriptionsError,
    reloadSubscriptions
  } = useWebhookSubscriptions(ecommerceId, isAdmin)

  const busy = generating || revealing || revoking

  if (loadingProfile) return <ReverLoading />
  // Admin-only; the API refuses a non-admin caller regardless of what this page renders.
  if (!isAdmin) return <NotFound />
  if (loadingSecrets || loadingSubscriptions) return <ReverLoading />
  // An error must not render as "no secret": that reads as "signing is off" and would
  // invite generating a second secret that breaks the one already in use. The same goes
  // for subscriptions: "unsubscribed" would invite re-saving over live configuration.
  if (secretsError || subscriptionsError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <p className="text-sm text-neutral-600">
          {t('webhook_secret.load_error')}
        </p>
        <Base.Button
          type="button"
          variant="secondary"
          onClick={() => {
            if (secretsError) reloadSecrets()
            if (subscriptionsError) reloadSubscriptions()
          }}
        >
          {t('webhook_secret.retry')}
        </Base.Button>
      </div>
    )
  }

  // Whichever the last action produced: generation returns the value once, revealing
  // fetches the stored one on demand.
  const visibleSecret = generatedSecret ?? revealedSecret

  const regenerate = async () => {
    if (
      activeSecret &&
      !(await showDialog({
        title: t('webhook_secret.regenerate_confirm_title'),
        message: t('webhook_secret.regenerate_confirm'),
        okButtonText: t('webhook_secret.regenerate'),
        destructive: true
      }))
    ) {
      return
    }
    clearRevealed()
    await generateSecret()
  }

  const revoke = async () => {
    const confirmed = await showDialog({
      title: t('webhook_secret.revoke_confirm_title'),
      message: t('webhook_secret.revoke_confirm'),
      okButtonText: t('webhook_secret.revoke'),
      destructive: true
    })
    if (!confirmed) return
    clearRevealed()
    await revokeSecret()
  }

  return (
    <>
      {isFlagEnabled && <BlockedDashboardOverlay />}
      <PageHeader
        title={t('webhook_subscriptions.title')}
        description={t('webhook_subscriptions.description')}
      />
      <div className="flex flex-col gap-4 px-7 py-4">
        <div className="flex max-w-[1000px] flex-col gap-4">
          <div className="rounded border border-solid border-neutral-200 bg-blue-50 p-4">
            <p className="text-sm">
              {t('webhook_subscriptions.auto_disable_notice')}{' '}
              <a
                href="https://docs.itsrever.com/apis/webhooks"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                {t('webhook_subscriptions.docs_link')}
              </a>
            </p>
          </div>
          <WebhookSubscriptionsForm
            ecommerceId={ecommerceId}
            saved={subscriptions}
          />
        </div>

        <hr className="max-w-[1000px] border-neutral-200" />

        <div className="flex max-w-[1000px] flex-col gap-4">
          <div>
            <h2 className="font-roobert text-base font-semibold leading-tight text-gray-900">
              {t('webhook_secret.title')}
            </h2>
            <p className="text-xs text-gray-500">
              {t('webhook_secret.description')}
            </p>
          </div>

          <GroupScopeNotice group={group} ecommerceIds={groupEcommerceIds} />

          {activeSecret ? (
            <div className="flex flex-col gap-2 rounded border border-neutral-200 p-4">
              <p className="text-sm">
                <span className="font-medium">
                  {t('webhook_secret.active')}:
                </span>{' '}
                <code>{activeSecret.redacted_value}</code>
              </p>
              <p className="text-xs text-neutral-500">
                {t('webhook_secret.created', {
                  date: activeSecret.created_at
                    ? new Date(activeSecret.created_at).toLocaleString()
                    : '—',
                  ...RAW_TEXT
                })}
              </p>
              {visibleSecret ? (
                <SecretValue value={visibleSecret} />
              ) : (
                <div>
                  <Base.Button
                    type="button"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => revealSecret()}
                  >
                    {t('webhook_secret.reveal')}
                  </Base.Button>
                </div>
              )}
            </div>
          ) : (
            <Base.Card variant="muted" className="p-4">
              <p className="text-sm">{t('webhook_secret.none')}</p>
            </Base.Card>
          )}

          <div className="flex gap-2">
            <Base.Button
              type="button"
              variant="primary"
              disabled={busy}
              onClick={regenerate}
            >
              {activeSecret
                ? t('webhook_secret.regenerate')
                : t('webhook_secret.generate')}
            </Base.Button>
            {activeSecret ? (
              <Base.Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={revoke}
              >
                {t('webhook_secret.revoke')}
              </Base.Button>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

// The draft is local so toggles and the URL are edited freely and land together on Save:
// the API validates the pair (enabling requires a URL), so sending each flip on change
// would reject half-finished edits.
function WebhookSubscriptionsForm({
  ecommerceId,
  saved
}: {
  ecommerceId: string
  saved: WebhookSubscriptions
}) {
  const { t } = useTranslation()
  const { saveSubscriptions, saving } = useSaveWebhookSubscriptions(ecommerceId)
  const { sendTests, testing, testResults, clearTestResults } =
    useSendTestWebhooks(ecommerceId)
  const showDialog = useDialog()
  const [draft, setDraft] = useState<WebhookSubscriptions>(saved)

  // Field-level deps: `saved` is rebuilt every render, and resetting on identity would
  // clobber what the user is typing.
  useEffect(() => {
    setDraft({
      onStartEnabled: saved.onStartEnabled,
      onCompletedEnabled: saved.onCompletedEnabled,
      url: saved.url
    })
  }, [saved.onStartEnabled, saved.onCompletedEnabled, saved.url])

  const anyEnabled = draft.onStartEnabled || draft.onCompletedEnabled
  const missingUrl = anyEnabled && draft.url.trim() === ''
  // The URL only exists to serve a subscription, so with none marked it is not editable
  // and never saved on its own — a URL-only save would change nothing deliverable.
  const url = anyEnabled ? draft.url.trim() : saved.url
  const changes = [
    draft.onStartEnabled !== saved.onStartEnabled,
    draft.onCompletedEnabled !== saved.onCompletedEnabled,
    url !== saved.url
  ].filter(Boolean).length
  // The draft, not what is saved: the point is to check a URL before subscribing to it.
  const testableWebhooks: TestableWebhook[] = [
    ...(draft.onStartEnabled ? ['onStart' as const] : []),
    ...(draft.onCompletedEnabled ? ['onCompleted' as const] : [])
  ]
  const busy = saving || testing

  const save = async () => {
    const unsubscribing =
      (saved.onStartEnabled && !draft.onStartEnabled) ||
      (saved.onCompletedEnabled && !draft.onCompletedEnabled)
    if (
      unsubscribing &&
      !(await showDialog({
        title: t('webhook_subscriptions.unsubscribe_confirm_title'),
        message: t('webhook_subscriptions.unsubscribe_confirm'),
        okButtonText: t('webhook_subscriptions.save')
      }))
    ) {
      return
    }
    await saveSubscriptions({ ...draft, url })
  }

  // Any edit makes the last test results describe a configuration that no longer exists.
  const editDraft = (
    update: (d: WebhookSubscriptions) => WebhookSubscriptions
  ) => {
    clearTestResults()
    setDraft(update)
  }

  // Dropping the last subscription also drops any half-typed URL, so re-enabling later
  // starts from the stored value instead of resurfacing an abandoned edit.
  const setToggle = (patch: Partial<WebhookSubscriptions>) =>
    editDraft((d) => {
      const next = { ...d, ...patch }
      if (!next.onStartEnabled && !next.onCompletedEnabled) next.url = saved.url
      return next
    })

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 rounded border border-neutral-200 p-4">
        <SubscriptionToggle
          label={t('webhook_subscriptions.on_start')}
          hint={t('webhook_subscriptions.on_start_hint')}
          checked={draft.onStartEnabled}
          disabled={busy}
          onChange={(checked) => setToggle({ onStartEnabled: checked })}
        />
        <SubscriptionToggle
          label={t('webhook_subscriptions.on_completed')}
          hint={t('webhook_subscriptions.on_completed_hint')}
          checked={draft.onCompletedEnabled}
          disabled={busy}
          onChange={(checked) => setToggle({ onCompletedEnabled: checked })}
        />

        <Base.FieldRoot>
          <Base.FieldLabel>
            {t('webhook_subscriptions.url_label')}
          </Base.FieldLabel>
          <Base.Input
            type="url"
            placeholder="https://example.com/webhooks/rever"
            value={draft.url}
            disabled={busy || !anyEnabled}
            onChange={(e) => {
              const value = e.target.value
              editDraft((d) => ({ ...d, url: value }))
            }}
          />
          <Base.FieldDescription>
            {!anyEnabled
              ? t('webhook_subscriptions.url_disabled_hint')
              : missingUrl
                ? t('webhook_subscriptions.url_required')
                : t('webhook_subscriptions.url_hint')}
          </Base.FieldDescription>
        </Base.FieldRoot>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <Base.Button
              type="button"
              variant="secondary"
              loading={testing}
              disabled={busy || !anyEnabled || missingUrl}
              onClick={() =>
                sendTests({ webhooks: testableWebhooks, url: draft.url.trim() })
              }
            >
              {t('webhook_subscriptions.test_button')}
            </Base.Button>
            <p className="text-xs text-neutral-600">
              {t('webhook_subscriptions.test_hint')}
            </p>
          </div>
          {testResults?.map((result) => (
            <WebhookTestOutcome key={result.webhook} result={result} />
          ))}
        </div>
      </div>
      <SaveBar
        changes={changes}
        problem={
          missingUrl ? t('webhook_subscriptions.url_required') : undefined
        }
        saving={saving}
        onDiscard={() => editDraft(() => ({ ...saved }))}
        onSave={save}
      />
    </div>
  )
}

function WebhookTestOutcome({ result }: { result: WebhookTestResult }) {
  const { t } = useTranslation()
  const { response } = result
  const values = {
    event: t(
      result.webhook === 'onStart'
        ? 'webhook_subscriptions.on_start'
        : 'webhook_subscriptions.on_completed'
    ),
    status: response?.status_code ?? 0,
    error: response?.error ?? '',
    ...RAW_TEXT
  }
  const delivered = response?.delivered ?? false

  // Proto3 JSON omits defaults, so a delivered sample comes back with no failure at all.
  const message = (() => {
    if (!response) return t('webhook_subscriptions.test_request_failed', values)
    if (delivered) return t('webhook_subscriptions.test_delivered', values)
    switch (response.failure) {
      case PbSendTestWebhookResponseFailureEnum.Rejected:
        return t('webhook_subscriptions.test_rejected', values)
      case PbSendTestWebhookResponseFailureEnum.Timeout:
        return t('webhook_subscriptions.test_timeout', values)
      case PbSendTestWebhookResponseFailureEnum.Redirect:
        return t('webhook_subscriptions.test_redirect', values)
      case PbSendTestWebhookResponseFailureEnum.NonPublicAddress:
        return t('webhook_subscriptions.test_non_public', values)
      default:
        return t('webhook_subscriptions.test_unreachable', values)
    }
  })()

  return (
    <div className="flex flex-col gap-1">
      <p
        className={`text-xs ${delivered ? 'text-success-main' : 'text-red-500'}`}
      >
        {message}
      </p>
      {!delivered && response?.response_body ? (
        <code className="break-all rounded bg-neutral-100 p-2 text-xs">
          {response.response_body}
        </code>
      ) : null}
    </div>
  )
}

function SubscriptionToggle({
  label,
  hint,
  checked,
  disabled,
  onChange
}: {
  label: string
  hint: string
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-neutral-500">{hint}</p>
      </div>
      <Base.Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={label}
      />
    </div>
  )
}

// The secret is issued per group, so generating or deleting it here changes signing for
// every store of the group. Nothing on a single-store dashboard shows that, so the scope
// is spelled out rather than left to be discovered by a sibling store breaking.
function GroupScopeNotice({
  group,
  ecommerceIds
}: {
  group: string
  ecommerceIds: string[]
}) {
  const { t } = useTranslation()
  if (!group) return null

  return (
    <div className="flex flex-col gap-1 rounded border border-solid border-neutral-200 bg-blue-50 p-4">
      <p className="text-sm">
        {t('webhook_secret.group_scope', { group, ...RAW_TEXT })}
      </p>
      {ecommerceIds.length > 1 && (
        <p className="text-xs text-neutral-600">
          {t('webhook_secret.group_ecommerces', {
            ecommerces: ecommerceIds.join(', '),
            ...RAW_TEXT
          })}
        </p>
      )}
    </div>
  )
}

function SecretValue({ value }: { value: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  return (
    <div className="flex items-center gap-2">
      <code className="grow break-all rounded bg-neutral-100 p-2 text-xs">
        {value}
      </code>
      <Base.Button
        type="button"
        variant="secondary"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1200)
          } catch {
            setCopied(false)
          }
        }}
      >
        {copied ? t('webhook_secret.copied') : t('webhook_secret.copy')}
      </Base.Button>
    </div>
  )
}
